import { Button, Chip, Typography } from "@mui/material";
import { useState } from "react";
import {
  Datagrid,
  FunctionField,
  List,
  SearchInput,
  TextField,
  useCanAccess,
  useLocaleState,
  useTranslate,
} from "react-admin";

import { IS_PROD_TARGET } from "../config";
import { ROWS_PER_PAGE } from "../constants";
import { TEST_CUSTOMERS_RESOURCE } from "../constants/resources";
import { formatDateTimeInline } from "../lib/dateUtils";
import { getInvitationStatusColor } from "../lib/statusColors";
import { InvitationRecord } from "../types/invitation";
import { OrganizationAccountLink } from "./OrganizationAccountLink";
import { TestAccountDialog } from "./TestAccountDialog";

const InvitationStatusField = ({ record }: { record?: { status: string } }) => {
  const translate = useTranslate();

  if (!record) return null;

  return (
    <Chip
      label={translate(`invitationStatus.${record.status.toLowerCase()}`)}
      color={getInvitationStatusColor(record.status)}
      size="small"
    />
  );
};

const invitationFilters = [<SearchInput source="search" placeholder="Nach E-Mail oder Account suchen" alwaysOn />];

// Invitations are sent from the B2B account page in the CRM view; this list
// shows them across accounts.
export const InvitationList = () => {
  const [locale] = useLocaleState();
  const translate = useTranslate();
  const [creatingTestAccount, setCreatingTestAccount] = useState(false);
  const { canAccess, error } = useCanAccess({ action: "create", resource: TEST_CUSTOMERS_RESOURCE });
  const canCreateTestCustomer = !IS_PROD_TARGET && !error && canAccess === true;

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} data-testid="invitation-list-hint">
        {translate("Invitation.list.hint")}
        {canCreateTestCustomer && (
          <Button size="small" onClick={() => setCreatingTestAccount(true)} sx={{ ml: 1, textTransform: "none" }}>
            {translate("testAccount.title")}
          </Button>
        )}
      </Typography>
      {canCreateTestCustomer && creatingTestAccount && (
        <TestAccountDialog onClose={() => setCreatingTestAccount(false)} />
      )}
      <List
        filters={invitationFilters}
        actions={false}
        title={translate("Invitation.list.title")}
        perPage={ROWS_PER_PAGE}
      >
        <Datagrid bulkActionButtons={false}>
          <TextField source="email" label={translate("Invitation.fields.email")} />
          <FunctionField
            label={translate("Invitation.fields.account")}
            render={(record: InvitationRecord) => (
              <OrganizationAccountLink
                salesforceAccountId={record.salesforceAccountId ?? null}
                salesforceAccountName={record.salesforceAccountName ?? null}
              />
            )}
          />
          <FunctionField
            label={translate("Invitation.fields.status")}
            render={record => <InvitationStatusField record={record} />}
          />
          <FunctionField
            label={translate("Invitation.fields.createdAt")}
            render={(record: InvitationRecord) => formatDateTimeInline(record.createdAt, locale)}
          />
        </Datagrid>
      </List>
    </>
  );
};
